import { DicomMessageLevel, type DicomSeries } from '@/lib/dicom/DicomSeries';
import { IMAGE_TYPE_VRT } from '@/lib/dicom/DicomTags';
import { logger } from '@/util/logger';
import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import ContentType from '@/api/mimetype';
import { DicomInfoUtil } from '@/lib/dicom/DicomInfoUtil';
import { dicomFileName as filename } from '@/lib/dicom/dicomFileName';
import { BooleanFieldValues } from '@/lib/dicom/types';
import { isFiniteNumber } from '@/util/isFiniteNumber';
import { DicomMessages } from '@/lib/dicom/dicomMessages';

/**
 * Per-file (per-{@link DicomInfo}) and per-series content checks: modality,
 * photometric interpretation, image type, image size, annotations, transfer
 * syntax and image position.
 */

const log = logger('DicomFileChecks');

type DicomInfoCheck = (dicomInfo: DicomInfo) => void;

/**
 *  The commonly known name for the DICOMDIR file that is usually located at the
 *  root of the DICOM export filesystem.
 *
 *  This is considered to be a case-sensitive filename.
 */
export const DicomdirFilename = 'DICOMDIR';

/**
 * Arbitrary size chosen (100 kbytes)
 */
export const MIN_PIXEL_DATA_LENGTH = 100 * 1024;

/**
 * Shared plumbing over a {@link DicomSeries}: its non-excluded items. A free
 * function rather than a class member because it belongs to neither check class
 * — both {@link DicomFileChecks} and DicomSeriesGeometryChecks consume it.
 */
export function filterExcluded(group: DicomSeries): DicomInfo[] {
    return group.items.filter((dicomInfo) => !dicomInfo.isExcluded);
}

export class DicomFileChecks {
    /**
     * An iterator over non excluded dicom info
     */
    static eachNonExcludedDicomInfo(group: DicomSeries, fn: DicomInfoCheck): void {
        if (!group.isExcluded) {
            filterExcluded(group).forEach(fn);
        }
    }

    /**
     * Ignore DICOM files with the name 'DICOMDIR'.
     */
    static checkExcludeDicomdir(dicomInfo: DicomInfo): void {
        if (filename(dicomInfo) === DicomdirFilename) {
            if (dicomInfo.fileType && dicomInfo.fileType.mime === ContentType.Dicom) {
                log.info('Found %s', DicomdirFilename);
                DicomMessages.excludeWithInfoMessage(dicomInfo, `${DicomdirFilename} file ignored`);
            }
        }
    }

    /**
     * Check that the file has a modality of 'CT'.
     *
     * This will exclude files that have PDF or DOC files in them
     * as well as other types of scans.
     */
    static checkModalityIsCt(dicomInfo: DicomInfo): void {
        if (dicomInfo.modality) {
            if (dicomInfo.modality === 'CT') {
                // Ok
            } else {
                DicomMessages.excludeWithInfoMessage(
                    dicomInfo,
                    `DICOM file '${filename(dicomInfo)}' with modality of '${dicomInfo.modality}' ignored`,
                );
            }
        } else {
            DicomMessages.appendMessage(
                dicomInfo.messages,
                DicomMessageLevel.Warning,
                `DICOM file '${filename(dicomInfo)}' has no modality (assuming CT)`,
            );
        }
    }

    /**
     * The photometric interpretation is a mandatory field.
     *
     * WARNING: historically the anonymisation code has stripped this field making reimporting
     * the DICOM series unavailable.
     *
     * (00028,0004) Photometric
     */
    static checkHasPhotometricInterpretation(dicomInfo: DicomInfo): void {
        if (dicomInfo.photometricInterpretation) {
            // Monochrome1 is a negative, monochrome2 is a positive image
            if (
                ['MONOCHROME2'].some(
                    (s: string): boolean => s === dicomInfo.photometricInterpretation,
                )
            ) {
                // Ok
            } else {
                DicomMessages.excludeWithInfoMessage(
                    dicomInfo,
                    `DICOM file '${filename(dicomInfo)}' with photometric interpretation of '${dicomInfo.photometricInterpretation}' ignored`,
                );
            }
        } else {
            DicomMessages.excludeWithWarningMessage(
                dicomInfo,
                `DICOM file '${filename(dicomInfo)}' has no photometric interpretation`,
            );
        }
    }

    /**
     * Check that the files are not 3d models.
     */
    static checkForVolumeRenderingTechnique(dicomInfo: DicomInfo): void {
        if (dicomInfo.imageType.includes(IMAGE_TYPE_VRT)) {
            DicomMessages.excludeWithInfoMessage(
                dicomInfo,
                `Volume image in file ${filename(dicomInfo)} ignored`,
            );
        }
    }

    /**
     * Check that the image inside the DICOM file is present and is larger than
     * some arbitrary size (e.g. 100kbytes).
     *
     * The actual size isn't important, except we want to exclude obviously invalid images
     * that are not CT images.
     *
     * @see {@link https://dicom.innolitics.com/ciods/us-image/image-pixel/7fe00010}
     */
    static checkImageSizes(group: DicomSeries): void {
        if (!group.isExcluded) {
            filterExcluded(group).forEach((dicomInfo) => {
                if (!dicomInfo.pixelDataLength) {
                    DicomMessages.excludeWithErrorMessage(
                        group,
                        `No pixel data in some DICOM files.`,
                    );
                    DicomMessages.excludeWithErrorMessage(
                        dicomInfo,
                        `No pixel data in ${filename(dicomInfo)}`,
                    );
                } else if (dicomInfo.pixelDataLength < MIN_PIXEL_DATA_LENGTH) {
                    DicomMessages.excludeWithInfoMessage(
                        dicomInfo,
                        `Small image (pixel data) ${filename(dicomInfo)} is excluded`,
                    );
                } // else it is assumed the pixel data is valid
            });
        }
    }

    /**
     * Check if the 'BurnedInAnnotation' flag is set. These files should be excluded
     * for the following reasons:
     *
     *  - the annotation can't be anonymised. The client size processing is intended to
     *  only upload anonymised data to the server.
     *
     *  - the annotation will mean that the machine reading of the images will have less
     *  than ideal outcomes as the training does not include annotations.
     */
    static checkForBurnedInAnnotation(dicomInfo: DicomInfo): void {
        if (dicomInfo.burnedInAnnotation === BooleanFieldValues.Yes) {
            DicomMessages.excludeWithInfoMessage(
                dicomInfo,
                `Image in file ${filename(dicomInfo)} contains annotations`,
            );
        }
    }

    /**
     * Enumerate all images in the series and exclude the series if an on the images
     * are compressed (i.e. not the raw format)
     */
    static checkCompressedImageFormat(group: DicomSeries): void {
        const allowed = [
            '1.2.840.10008.1.2', //      Implicit VR Endian: Default Transfer Syntax for DICOM
            '1.2.840.10008.1.2.1', //    Explicit VR Little Endian
            '1.2.840.10008.1.2.1.99', // Deflated Explicit VR Little Endian
            '1.2.840.10008.1.2.2', //    Explicit VR Big Endian
        ];

        if (!group.isExcluded) {
            group.items.forEach((i) => {
                if (i.transferSyntaxUid) {
                    if (!allowed.includes(i.transferSyntaxUid)) {
                        DicomMessages.excludeWithInfoMessage(
                            group,
                            `Image with non-raw transfer syntax found (${i.transferSyntaxUid})`,
                        );
                        //  early return, as the series is marked as excluded.
                    } // else OK
                } // else there is no transfer syntax to check
            });
        }
    }

    /**
     * Check that the image position patient is valid. Without this the
     * images cannot be sorted.
     */
    static checkImagePositionPatient(dicomInfo: DicomInfo): void {
        if (isFiniteNumber(DicomInfoUtil.verticalImagePositionPatient(dicomInfo))) {
            // ok
        } else {
            DicomMessages.excludeWithInfoMessage(
                dicomInfo,
                `Image '${filename(dicomInfo)}' has invalid image position patient`,
            );
        }
    }
}
